<template>
  <div
    class="search-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="search-overlay-title"
    aria-describedby="search-overlay-subtitle"
    v-scroll-lock="true"
    @keydown="onKeydown"
  >
    <div class="search-panel" :style="{ paddingTop: panelOffset + 'px' }">
      <div class="search-panel-inner">
        <button
          type="button"
          class="search-close"
          :aria-label="toLocal('search.close')"
          @click="close"
        >
          <img src="@/assets/icons/close_cross.svg" alt="" aria-hidden="true" />
        </button>

        <h2 id="search-overlay-title" class="search-title">
          {{ toLocal("search.title") }}
        </h2>
        <p id="search-overlay-subtitle" class="search-subtitle">
          {{ toLocal("search.subtitle") }}
        </p>
        <p class="search-context">{{ contextText }}</p>

        <form class="search-form" role="search" @submit.prevent="submit">
          <div
            class="search-field"
            :class="{ invalid: status === 'error' }"
          >
            <img
              class="search-field-icon"
              src="@/assets/icons/search.svg"
              alt=""
              aria-hidden="true"
            />
            <input
              ref="searchInput"
              v-model="query"
              type="search"
              class="search-input"
              :placeholder="toLocal('search.placeholder')"
              :aria-label="toLocal('search.input_label')"
              :aria-invalid="status === 'error'"
              :aria-describedby="status === 'error' ? 'search-error' : null"
              autocomplete="off"
            />
            <button
              v-if="query"
              type="button"
              class="search-clear-input"
              :aria-label="toLocal('search.clear_input')"
              @click="clearInput"
            >
              <img
                src="@/assets/icons/close_cross.svg"
                alt=""
                aria-hidden="true"
              />
            </button>
          </div>
          <button type="submit" class="search-submit">
            {{ toLocal("search.button") }}
          </button>
        </form>

        <div class="search-status" aria-live="polite">
          <p
            v-if="status === 'error'"
            id="search-error"
            class="search-error"
            role="alert"
          >
            <span class="search-error-mark" aria-hidden="true">!</span>
            {{ toLocal("search.error_empty") }}
          </p>

          <div v-else-if="status === 'searching'" class="search-searching">
            <span class="search-spinner" aria-hidden="true"></span>
            <span class="search-searching-text">
              {{ toLocal("search.searching") }}
            </span>
          </div>

          <template v-else-if="status === 'results'">
            <div class="search-results-head">
              <p class="search-found">{{ foundText }}</p>
              <button type="button" class="search-clear" @click="clearSearch">
                {{ toLocal("search.clear") }}
              </button>
            </div>
            <ul class="search-results">
              <li
                v-for="doc in results"
                :key="doc.path"
                class="search-result"
              >
                <a
                  class="search-result-link"
                  :href="resultHref(doc)"
                  :target="opensInNewTab(doc) ? '_blank' : null"
                  :rel="isExternal(doc.path) ? 'noopener noreferrer' : null"
                  :download="isDownload(doc) ? downloadName(doc) : null"
                >
                  <span class="search-badge" aria-hidden="true">{{
                    resultType(doc.path)
                  }}</span>
                  <span class="search-result-text">
                    <span class="search-result-title">{{
                      toLocal(doc.title)
                    }}</span>
                    <span class="search-result-desc">{{
                      toLocal(doc.description)
                    }}</span>
                  </span>
                </a>
              </li>
            </ul>
          </template>

          <div v-else-if="status === 'empty'" class="search-empty">
            <p class="search-empty-title">{{ toLocal("search.no_results") }}</p>
            <p class="search-empty-text">{{ noResultsText }}</p>
            <button
              type="button"
              class="search-empty-clear"
              @click="clearSearch"
            >
              {{ toLocal("search.clear") }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="search-backdrop" aria-hidden="true" @click="close"></div>
  </div>
</template>

<script>
import searchIndex from "@/assets/data/search_index.json";
import {
  searchDocuments,
  resultType,
  isExternal,
  isVideo,
  resolvePath,
} from "@/methods/search.js";

const SEARCH_DELAY = 400;

export default {
  name: "SearchOverlay",
  data() {
    return {
      query: "",
      status: "idle", // idle | error | searching | results | empty
      results: [],
      lastQuery: "",
      headerOffset: 0,
      previouslyFocused: null,
      searchIndex: searchIndex,
      languageNames: {
        CZ: "Čeština",
        DE: "Deutsch",
        NL: "Dutch",
        EN: "English",
        ES: "Español",
        FR: "Français",
        PL: "Polski",
        EE: "Eesti",
        LV: "Latviešu",
        HU: "Magyar",
        IT: "Italiano",
        PT: "Português",
        LT: "Lietuvių",
        MT: "Malti",
      },
    };
  },
  computed: {
    panelOffset() {
      return this.headerOffset;
    },
    contextText() {
      const lang = this.$store.state.lang;
      return this.toLocal("search.context").replace(
        "{language}",
        this.languageNames[lang] || lang
      );
    },
    foundText() {
      const key =
        this.results.length === 1 ? "search.found_one" : "search.found_other";
      return this.toLocal(key).replace("{count}", this.results.length);
    },
    noResultsText() {
      return this.toLocal("search.no_results_text").replace(
        "{term}",
        this.lastQuery
      );
    },
  },
  watch: {
    query() {
      if (this.status === "error" && this.query.trim()) {
        this.status = "idle";
      }
    },
    "$route.path"() {
      this.close();
    },
    windowWidth() {
      this.measureHeader();
    },
  },
  mounted() {
    this.previouslyFocused = document.activeElement;
    this.measureHeader();
    this.setBackgroundInert(true);
    this.$nextTick(() => {
      if (this.$refs.searchInput) this.$refs.searchInput.focus();
    });
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.setBackgroundInert(false);
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }
  },
  methods: {
    resultType,
    isExternal,
    resultHref(doc) {
      return resolvePath(doc.path);
    },
    opensInNewTab(doc) {
      return isExternal(doc.path) || isVideo(doc.path);
    },
    isDownload(doc) {
      return !this.opensInNewTab(doc);
    },
    measureHeader() {
      const header = document.querySelector(".header-container");
      this.headerOffset = header ? header.offsetHeight : 0;
    },
    setBackgroundInert(inert) {
      ["#main-content", ".footer-container", "#go-to-top"].forEach(
        (selector) => {
          const el = document.querySelector(selector);
          if (!el) return;
          if (inert) {
            el.setAttribute("inert", "");
            el.setAttribute("aria-hidden", "true");
            if (selector === "#go-to-top") el.style.visibility = "hidden";
          } else {
            el.removeAttribute("inert");
            el.removeAttribute("aria-hidden");
            if (selector === "#go-to-top") el.style.visibility = "";
          }
        }
      );
    },
    onKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusables = this.$el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    submit() {
      const term = this.query.trim();
      if (!term) {
        this.status = "error";
        this.$refs.searchInput.focus();
        return;
      }
      this.lastQuery = term;
      this.status = "searching";
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.results = searchDocuments(term, this.searchIndex, this.toLocal);
        this.status = this.results.length ? "results" : "empty";
      }, SEARCH_DELAY);
    },
    clearInput() {
      this.query = "";
      if (this.status === "error") this.status = "idle";
      this.$refs.searchInput.focus();
    },
    clearSearch() {
      this.query = "";
      this.results = [];
      this.status = "idle";
      this.$refs.searchInput.focus();
    },
    downloadName(doc) {
      return `${this.toLocal(doc.title)}.${doc.path.split(".").pop()}`;
    },
    close() {
      this.$store.state.searchOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

$search-red: #c73b33;
$search-ink: #0f1429;
$search-muted: #52596e;
$search-border: #babfcc;
$search-fill: #f0f2f7;

.search-overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  flex-direction: column;
  background: rgba(23, 23, 23, 0.6);
}

.search-backdrop {
  flex: 1 1 auto;
}

.search-panel {
  background: #ffffff;
  flex: 0 0 auto;
  max-height: 100%;
  overflow-y: auto;
  @media (max-width: 767px) {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.search-panel-inner {
  position: relative;
  width: 100%;
  padding: 26px 120px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 1199px) {
    padding: 24px 56px;
  }
  @media (max-width: 767px) {
    flex: 1 1 auto;
    padding: 32px 24px;
    gap: 16px;
  }
}

.search-close {
  position: fixed;
  right: 24px;
  width: 44px;
  height: 44px;
  padding: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  @media (max-width: 767px) {
    display: none;
  }
  img {
    width: 24px;
    height: 24px;
    padding: 2px;
    display: block;
  }
}

.search-title {
  margin: 0;
  font-family: $semiBoldFont;
  font-size: clamp(32px, 3.2vw, 46px);
  color: $search-ink;
  line-height: 1.25;
}

.search-subtitle {
  margin: 0;
  font-size: clamp(19px, 1.5vw, 21px);
  color: $search-muted;
  line-height: 1.35;
}

.search-context {
  margin: 0;
  font-size: clamp(15px, 1.25vw, 18px);
  color: $search-muted;
}

.search-form {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-top: 4px;
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 16px;
  }
}

.search-field {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  min-height: 54px;
  padding-left: 18px;
  padding-right: 10px;
  background: #ffffff;
  border: 1px solid $search-border;
  border-radius: 6px;
  &.invalid {
    border: 2px solid $search-red;
  }
  @media (max-width: 767px) {
    width: 100%;
    border-radius: 8px;
    min-height: 54px;
  }
}

.search-field-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  padding: 0 6px;
  font-family: $mainFont;
  font-size: 19px;
  color: $search-ink;
  background: transparent;
  &::placeholder {
    color: $search-muted;
    opacity: 1;
  }
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }
  @media (max-width: 767px) {
    font-size: 18px;
  }
}

.search-field:focus-within {
  border: 2px solid $search-red;
}

.search-clear-input {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  img {
    width: 20px;
    height: 20px;
    display: block;
  }
}

.search-submit {
  flex-shrink: 0;
  min-width: 184px;
  min-height: 54px;
  border: none;
  border-radius: 6px;
  background: $search-red;
  color: #ffffff;
  font-family: $semiBoldFont;
  font-size: 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background: #a93228;
  }
  @media (max-width: 767px) {
    width: 100%;
    min-height: 52px;
    border-radius: 8px;
    font-size: 18px;
  }
}

.search-status {
  margin-top: 6px;
}

.search-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: $semiBoldFont;
  font-size: 16px;
  color: $search-red;
  .search-error-mark {
    font-size: 17px;
  }
}

.search-searching {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 62px;
  padding-left: 18px;
  background: $search-fill;
  border-radius: 6px;
  .search-searching-text {
    font-family: $semiBoldFont;
    font-size: 18px;
    color: $search-ink;
  }
  @media (max-width: 767px) {
    flex-direction: column;
    justify-content: center;
    min-height: 180px;
    padding: 0;
    border-radius: 16px;
    .search-searching-text {
      font-size: 19px;
    }
  }
}

.search-spinner {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: 3px solid rgba($search-red, 0.25);
  border-top-color: $search-red;
  border-radius: 50%;
  animation: search-spin 0.8s linear infinite;
  @media (max-width: 767px) {
    width: 34px;
    height: 34px;
  }
}

@keyframes search-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-spinner {
    animation: none;
  }
}

.search-results-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  .search-found {
    margin: 0;
    font-family: $semiBoldFont;
    font-size: 20px;
    color: $search-ink;
    @media (max-width: 767px) {
      font-size: 18px;
    }
  }
  .search-clear {
    border: none;
    background: transparent;
    padding: 4px;
    font-family: $mainFont;
    font-size: 16px;
    color: $search-ink;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}

.search-results {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-result-link {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 90px;
  padding: 10px 18px 10px 16px;
  border: 1px solid $search-border;
  border-radius: 7px;
  text-decoration: none;
  transition: border-color 0.2s;
  &:hover {
    border-color: $search-red;
    .search-result-title {
      text-decoration: underline;
    }
  }
  @media (max-width: 767px) {
    min-height: 104px;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
  }
}

.search-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  background: $search-fill;
  border-radius: 6px;
  font-family: $semiBoldFont;
  font-size: 12px;
  color: $search-red;
  text-align: center;
  @media (max-width: 767px) {
    width: 44px;
    height: 44px;
    font-size: 11px;
  }
}

.search-result-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  .search-result-title {
    font-family: $semiBoldFont;
    font-size: 21px;
    color: $search-ink;
    @media (max-width: 767px) {
      font-size: 17px;
    }
  }
  .search-result-desc {
    font-size: 15px;
    color: $search-muted;
    @media (max-width: 767px) {
      font-size: 13px;
    }
  }
}

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 24px 28px 22px;
  background: $search-fill;
  border: 1px solid $search-border;
  border-radius: 8px;
  .search-empty-title {
    margin: 0;
    font-family: $semiBoldFont;
    font-size: 28px;
    color: $search-ink;
    @media (max-width: 767px) {
      font-size: 24px;
    }
  }
  .search-empty-text {
    margin: 0;
    font-size: 18px;
    color: $search-muted;
    @media (max-width: 767px) {
      font-size: 16px;
    }
  }
  .search-empty-clear {
    margin-top: 6px;
    min-width: 178px;
    min-height: 42px;
    padding: 8px 20px;
    background: #ffffff;
    border: 1px solid $search-red;
    border-radius: 6px;
    font-family: $semiBoldFont;
    font-size: 17px;
    color: $search-red;
    cursor: pointer;
    &:hover {
      background: #fdf1f0;
    }
  }
}
</style>
